import { useState, type FormEvent } from 'react'
import { motion } from 'framer-motion'
import { Eye, EyeOff, KeyRound } from 'lucide-react'
import axios from 'axios'
import { confirmPasswordReset } from '../api'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Card, CardContent, CardHeader } from './ui/card'

// Página de destino del enlace emailado — deliberadamente NO depende de
// AuthContext (un visitante sin sesión llega acá desde su correo).
export function ResetPassword() {
  const token = new URLSearchParams(window.location.search).get('token')
  const [newPassword, setNewPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (!token) return
    setError(null)
    setIsSubmitting(true)
    try {
      await confirmPasswordReset(token, newPassword)
      setSuccess(true)
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        setError('El enlace es inválido o ya expiró. Solicitá uno nuevo desde la pantalla de inicio de sesión.')
      } else {
        setError('Algo salió mal. Intenta de nuevo.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 px-4">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <Card>
          <CardHeader>
            <h1 className="text-lg font-semibold tracking-tight">Elegir nueva contraseña</h1>
          </CardHeader>
          <CardContent>
            {!token ? (
              <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">Enlace inválido o incompleto.</p>
            ) : success ? (
              <>
                <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
                  Contraseña actualizada. Ya podés iniciar sesión.
                </p>
                <Button
                  type="button"
                  className="mt-4 w-full"
                  onClick={() => {
                    window.location.href = '/'
                  }}
                >
                  Ir a iniciar sesión
                </Button>
              </>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    placeholder="Nueva contraseña"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pr-9"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {error && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">{error}</p>}
                <Button type="submit" disabled={isSubmitting} className="mt-1">
                  <KeyRound className="h-4 w-4" />
                  Actualizar contraseña
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}
